import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError, api } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { Alert, Field } from '../components/Ui'
import { de, fmt } from '../i18n/de'

export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [reveal, setReveal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setBusy(true)

    try {
      const result = await login(email, password, rememberMe)
      navigate(result.mustChangePassword ? '/password' : '/', { replace: true })
    } catch (caught) {
      setError(describeLoginError(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-wrap" style={{ minHeight: '100vh' }}>
      <form className="card login-card" onSubmit={submit}>
        <div className="card-b">
          <div className="brand" style={{ justifyContent: 'center', marginBottom: 'var(--sp-5)' }}>
            <span className="logo">D</span> {de.app.name}
          </div>

          {error && (
            <div style={{ marginBottom: 'var(--sp-3)' }}>
              <Alert kind="warning">{error}</Alert>
            </div>
          )}

          <Field label={de.login.email}>
            <input type="email" value={email} autoComplete="username" autoFocus required
                   onChange={event => setEmail(event.target.value)} />
          </Field>

          <Field label={de.login.password}>
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <input type={reveal ? 'text' : 'password'} value={password} required
                     autoComplete="current-password"
                     onChange={event => setPassword(event.target.value)} />
              <button type="button" className="btn btn-ghost btn-icon" title={de.login.reveal}
                      onPointerDown={() => setReveal(true)}
                      onPointerUp={() => setReveal(false)}
                      onPointerLeave={() => setReveal(false)}>👁</button>
            </div>
          </Field>

          <label className="sm" style={{ display: 'flex', gap: 6, alignItems: 'center',
                                         marginBottom: 'var(--sp-4)' }}>
            <input type="checkbox" checked={rememberMe}
                   onChange={event => setRememberMe(event.target.checked)} />
            {de.login.rememberMe}
          </label>

          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={busy}>
            {busy ? de.app.loading : de.login.submit}
          </button>

          {/* Kein Link: solange das Zurücksetzen nicht geklärt ist, ist ein toter Link
              schlechter als ein klarer Satz. Siehe design/11-offene-fragen.md B1. */}
          <div className="help" style={{ textAlign: 'center', marginTop: 'var(--sp-4)' }}>
            {de.login.forgotten}
          </div>
        </div>
      </form>
    </div>
  )
}

function describeLoginError(error: unknown): string {
  if (!(error instanceof ApiError)) return de.errors.generic

  if (error.type === 'account_locked') {
    const seconds = Number(error.extensions.retryAfterSeconds ?? 900)
    return fmt(de.login.locked, { minutes: Math.max(1, Math.round(seconds / 60)) })
  }
  if (error.type === 'account_disabled') return de.login.disabled
  return de.login.failed
}

/**
 * Passwortwechsel. Erzwungen nach einem Einmalpasswort — bis dahin antworten alle anderen
 * Endpunkte mit 403 password_change_required.
 */
export function PasswordChangePage() {
  const { me, reload } = useAuth()
  const navigate = useNavigate()

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const forced = me?.mustChangePassword ?? false
  const longEnough = next.length >= 12
  const matching = next.length > 0 && next === repeat

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    if (!matching) {
      setError(de.passwordChange.mismatch)
      return
    }

    setBusy(true)
    try {
      await api.post('/auth/password', { currentPassword: current, newPassword: next })
      await reload()
      navigate('/', { replace: true })
    } catch (caught) {
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : de.errors.generic)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-wrap" style={{ minHeight: '100vh' }}>
      <form className="card login-card" onSubmit={submit}>
        <div className="card-b">
          <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-lg)' }}>
            {de.passwordChange.title}
          </h3>

          {forced && (
            <div className="sm muted" style={{ marginBottom: 'var(--sp-4)' }}>
              {de.passwordChange.forced}
            </div>
          )}

          {error && (
            <div style={{ marginBottom: 'var(--sp-3)' }}>
              <Alert kind="warning">{error}</Alert>
            </div>
          )}

          <Field label={de.passwordChange.current}>
            <input type="password" value={current} required autoComplete="current-password"
                   onChange={event => setCurrent(event.target.value)} />
          </Field>

          <Field label={de.passwordChange.next} help={de.passwordChange.minLength}>
            <input type="password" value={next} required autoComplete="new-password"
                   onChange={event => setNext(event.target.value)} />
          </Field>

          <Field label={de.passwordChange.repeat}>
            <input type="password" value={repeat} required autoComplete="new-password"
                   onChange={event => setRepeat(event.target.value)} />
          </Field>

          {/* Stärkeanzeige als Rückmeldung, nicht als Hürde: keine
              Zusammensetzungsregeln. Siehe design/03-berechtigungen.md 3.4. */}
          <div className="pwbar">
            {Array.from({ length: 8 }, (_, index) => (
              <i key={index} className={index < Math.min(8, Math.floor(next.length / 2)) ? 'on' : ''} />
            ))}
          </div>
          {longEnough && <div className="check">✓ {de.passwordChange.lengthOk}</div>}

          <button type="submit" className="btn btn-primary"
                  style={{ width: '100%', marginTop: 'var(--sp-4)' }}
                  disabled={busy || !longEnough || !matching}>
            {de.app.save}
          </button>
        </div>
      </form>
    </div>
  )
}
